import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {coerceToPrimitive, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import {
  getConfiguredProcedureProductLimit,
  ProcedureProductLimitsConfigKey,
} from 'procedures/design/ProcedureDesignConstants';
import {MaterialInputType} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {ParameterField} from './ProcedurePlanTypes';

/** Schema field types that expect numeric values */
const NUMERIC_FIELD_TYPES: ReadonlyArray<string> = [FieldType.INTEGER, FieldType.FLOAT];

const setCustomEvalError = (evalResult: CustomEvalResult, error: ExcessUtil.CellError | null): void => {
  evalResult.error = evalResult.error ?? error;
};

export const validateNumericFieldValue = (
  evalResult: CustomEvalResult,
  typedValue: TypedValue,
  field: ParameterField,
  columnDefinition: ColumnDefinition,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): void => {
  // Only validate fields that expect numeric values
  if (!NUMERIC_FIELD_TYPES.includes(field.schemaField.fieldType)) {
    return;
  }

  // Multi-value arrays (plate map parameters) contain individually typed elements;
  // skip scalar numeric validation for these.
  if (isTypedValueEmpty(typedValue) || typedValue.type === BaseTypedValueType.ARRAY) {
    return;
  }

  const {resolvedValue, error: resolutionError} = resolveTypedValueToType(columnDefinition, typedValue);

  // If the resolution layer produced an error, surface it directly.
  if (resolutionError) {
    evalResult.val = resolvedValue;
    setCustomEvalError(evalResult, resolutionError);
    return;
  }

  // Check whether the resolved value's string representation is a valid number.
  const value = (resolvedValue as {value?: string} | null)?.value;
  if (value != null && value !== '' && Number.isNaN(Number(value))) {
    evalResult.val = resolvedValue;
    setCustomEvalError(evalResult, new ExcessUtil.ValueError('Value must be a number'));
  }
};

export const validateValueRange = (
  evalResult: CustomEvalResult,
  typedValue: TypedValue,
  field: ParameterField,
  columnDefinition: ColumnDefinition,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): void => {
  // customEval currently runs _before_ string resolution and this particular validation would like to
  // operate on a post-resolved value, so we manually fire it off first (See: BNCH-15744).
  const {resolvedValue} = resolveTypedValueToType(columnDefinition, typedValue);
  const value = coerceToPrimitive(resolvedValue);
  if (typeof value === 'number') {
    if (field.maximum?.floatValue != null && field.minimum?.floatValue != null) {
      // Maximum and minimum are both specified
      const outOfRangeWarning = `Value is out of the suggested range for this parameter: [${field.minimum.floatValue}, ${field.maximum.floatValue}]`;
      if (value > field.maximum.floatValue || value < field.minimum.floatValue) {
        setCustomEvalError(evalResult, new ExcessUtil.Warning(outOfRangeWarning));
      }
    } else if (field.maximum?.floatValue != null) {
      // Only a maximum is specified
      const outOfRangeWarning = `Value is above the suggested maximum for this parameter: ${field.maximum.floatValue}`;
      if (value > field.maximum.floatValue) {
        setCustomEvalError(evalResult, new ExcessUtil.Warning(outOfRangeWarning));
      }
    } else if (field.minimum?.floatValue != null) {
      // Only a minimum is specified
      const outOfRangeWarning = `Value is below the suggested minimum for this parameter: ${field.minimum.floatValue}`;
      if (value < field.minimum.floatValue) {
        setCustomEvalError(evalResult, new ExcessUtil.Warning(outOfRangeWarning));
      }
    }
  }
};

export const validateEquipment = (
  evalResult: CustomEvalResult,
  typedValue: TypedValue,
  field: ParameterField,
  resolveFromResolutionQuery: ResolveQueryFn
): void => {
  if (
    typedValue &&
    typedValue.type === FieldType.BIOENTITY_LINK &&
    typedValue.targetFiles.length > 0 &&
    field.allowedEquipmentModels
  ) {
    const {resolvedValue, error} = resolveFromResolutionQuery(
      typedValue,
      {
        type: ResolutionQueryType.CUSTOM,
        customResolutionQueryType: CustomResolutionQueryType.PROCEDURES_VALIDATE_EQUIPMENT_CONFIRMATION,
        bioentityId: typedValue.targetFiles[0].id ?? '',
        equipmentTypedValue: typedValue,
        modelTypedValue: field.allowedEquipmentModels,
      },
      makeTypedValueFromCustomResolvedValue
    );
    evalResult.val = resolvedValue;
    setCustomEvalError(evalResult, error);
  }
};

/** Limit the number of values represented by one logical procedure parameter input. */
export const validateNumberOfValues = (
  evalResult: CustomEvalResult,
  typedValue: TypedValue,
  field: ParameterField,
  columnDefinition: ColumnDefinition,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): void => {
  // customEval currently runs _before_ string resolution and this particular validation would like to
  // operate on a post-resolved value, so we manually fire it off first (See: BNCH-15744).
  const {resolvedValue} = resolveTypedValueToType(columnDefinition, typedValue);
  if (!resolvedValue) {
    return;
  }
  let numValues: number | undefined;
  switch (resolvedValue.type) {
    case BaseTypedValueType.ARRAY:
      numValues = resolvedValue.rawValue.length;
      break;
    case FieldType.BIOENTITY_LINK:
      numValues = resolvedValue.targetFiles.length;
      break;
    default:
      break;
  }
  const limit = getConfiguredProcedureProductLimit(ProcedureProductLimitsConfigKey.MAX_PARAMETER_VALUES);
  if (numValues !== undefined && limit != null && numValues > limit) {
    const error = new ExcessUtil.ValueError(`Only up to ${limit} values are supported`);
    // use the resolved value because we still want to display the chips in the cell
    evalResult.val = resolvedValue;
    setCustomEvalError(evalResult, error);
  }
};

export const validateArchivedEntity = (
  evalResult: CustomEvalResult,
  typedValue: TypedValue,
  archivedEntityIds: ReadonlySet<string>
): void => {
  if (archivedEntityIds.size === 0) {
    return;
  }
  // BIOENTITY_LINK is the standard format for entity references in procedure inputs.
  if (typedValue?.type === FieldType.BIOENTITY_LINK && typedValue.targetFiles.length > 0) {
    const archivedFile = typedValue.targetFiles.find(
      (targetFile) => targetFile.id != null && archivedEntityIds.has(targetFile.id)
    );
    if (archivedFile != null) {
      setCustomEvalError(evalResult, new ExcessUtil.ValueError(`${archivedFile.name} has been archived`));
    }
  }
};

export const validateMaterialInputConfirmationMethod = (
  evalResult: CustomEvalResult,
  typedValue: TypedValue,
  confirmationMethod: ProcedureParameterConfirmationMethod,
  dataType: MaterialInputType,
  resolveFromResolutionQuery: ResolveQueryFn
): void => {
  const {error} = resolveFromResolutionQuery(
    typedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType:
        CustomResolutionQueryType.PROCEDURES_VALIDATE_MATERIAL_INPUT_CONFIRMATION_METHOD_AND_DATA_TYPE,
      confirmationMethod,
      dataType,
    },
    makeTypedValueFromCustomResolvedValue
  );
  // It takes about a second for the load to complete - and while that's happening, the custom eval will
  // return a loading error. We ignore those errors to avoid the jarring experience of red dog ears on every
  // cell in the confirmation method column. It's very unlikely they can change things to a bad state and
  // submit in time before the custom eval loads, and even if it happens, we have backend validation.
  if (!(error instanceof ExcessUtil.LoadingError)) {
    setCustomEvalError(evalResult, error);
  }
};
